import { format } from '@pf/money';
import { Link, useParams } from '@tanstack/react-router';
import type { CSSProperties } from 'react';
import { useMe, useTrip, useTripCards } from '../app/data.ts';
import { cx } from '../kit/cx.ts';
import { DataTag, DateRangeRule } from '../kit/index.ts';
import t from '../kit/type.module.css';
import { amountIn, flag, time, today } from '../model/trip.ts';
import { zoneLabel } from '../model/zones.ts';
import s from './Timeline.module.css';

const JOURNEY = new Set(['flight', 'train', 'bus', 'ferry']);
const WEEKDAY = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' });
const weekday = (date: string) => WEEKDAY.format(new Date(`${date}T00:00:00Z`)).toUpperCase();
const hue = (n: number) => `var(--color-day-${((n - 1) % 8) + 1})`;

/** W-03 timeline: the whole trip as lanes (destinations, stays, moves, stops, tracks, spend). */
export function Timeline() {
  const { tripId } = useParams({ from: '/app/trips/$tripId/timeline' });
  const { model, error } = useTrip(tripId);
  const me = useMe();
  const code = useTripCards().cards.find((c) => c.id === tripId)?.code;
  if (error)
    return (
      <p className={cx(s.pad, t.bodyS)} role="alert">
        {error.message}
      </p>
    );
  if (!model || !me.data) return <p className={cx(s.pad, t.bodyS)}>Loading…</p>;
  const home = me.data.user.homeCurrency;
  const days = model.days;
  const n = Math.max(1, days.length);
  const col = (date: string | null) => (date ? days.findIndex((d) => d.date === date.slice(0, 10)) : -1);
  // Today at the destination; past days fill in, later ones stay grey.
  const now = today(days.find((d) => d.date === today(d.tz))?.tz ?? days[0]?.tz ?? null);
  const stops = days.reduce((a, d) => a + d.items.length, 0);
  const todayIndex = days.findIndex((d) => d.date === now);
  const walked = model.segments
    .filter((x) => x.userId === me.data.user.id && (x.mode === 'walk' || x.mode === 'run'))
    .reduce((a, x) => a + x.distanceM, 0);
  const spendBy = days.map((d) =>
    model.entries
      .filter((e) => !e.ownerUserId && e.dateLocal === d.date)
      .reduce((a, e) => a + (amountIn(e, home) ?? 0), 0),
  );
  const spent = model.entries.filter((e) => !e.ownerUserId).reduce((a, e) => a + (amountIn(e, home) ?? 0), 0);
  const maxSpend = Math.max(1, ...spendBy);
  const grid: CSSProperties = { gridTemplateColumns: `96px repeat(${n}, minmax(72px, 1fr))` };
  const span = (from: number, to: number): CSSProperties => ({
    gridColumn: `${from + 2} / span ${Math.max(1, to - from + 1)}`,
  });

  // Destinations: runs of days with the same destination.
  const runs: { name: string; currency: string | null; from: number; to: number }[] = [];
  days.forEach((d, i) => {
    const last = runs.at(-1);
    if (last && last.name === (d.destination?.name ?? '') && last.to === i - 1) last.to = i;
    else if (d.destination)
      runs.push({ name: d.destination.name, currency: d.destination.localCurrency, from: i, to: i });
  });
  const stays = model.reservations.filter((r) => r.kind === 'lodging');
  const title = (itemId: string | null) => {
    for (const p of [...days.flatMap((d) => d.items), ...model.lists.flatMap((l) => l.items)])
      if (p.item.id === itemId) return p.item.title;
    return 'Booking';
  };
  const filesFor = (itemId: string | null) =>
    model.attachments.filter((a) => a.ownerType === 'item' && a.ownerId === itemId).length;

  return (
    <div className={s.page}>
      {/* W-03's head: code and day, the trip name (its own case) and its dates on one line, the stats as tags. */}
      <header className={s.top}>
        <div>
          <div className={cx(t.displayS, s.muted)}>
            {[code, todayIndex >= 0 ? `Vol. ${String(todayIndex + 1).padStart(2, '0')}` : null]
              .filter(Boolean)
              .join(' · ')}
          </div>
          <div className={s.titleLine}>
            <h1 className={cx(t.titleL, s.title)}>
              <span className={s.bracket} aria-hidden="true">
                [
              </span>
              {model.title}
              <span className={s.bracket} aria-hidden="true">
                ]
              </span>
            </h1>
            {model.startDate && model.endDate && (
              <DateRangeRule start={flag(model.startDate)} end={flag(model.endDate)} live={todayIndex >= 0} />
            )}
          </div>
        </div>
        <div className={s.tags}>
          <DataTag label="Days" value={String(days.length)} />
          <DataTag label="Stops" value={String(stops)} />
          <DataTag label="Walked" value={`${(walked / 1000).toFixed(1)} km`} />
          <DataTag label="Spent" value={format(spent, home)} />
        </div>
      </header>

      <div className={s.scroll}>
        <section className={s.gantt} aria-label="Trip timeline">
          <div className={cx(s.lane, s.axis)} style={grid}>
            <div className={cx(t.readoutS, s.muted)}>{zoneLabel(days[0]?.tz ?? null, null)}</div>
            {days.map((d, i) => {
              // Ink flags where a destination starts; the other days by number and weekday (W-03).
              const starts = runs.some((r) => r.from === i);
              return (
                <div key={d.id} className={cx(s.dayHead, d.date === now && s.today)}>
                  {starts ? <span className={cx(t.readoutS, s.flag)}>{flag(d.date)}</span> : <span />}
                  <span className={cx(t.micro, d.date === now ? s.todayLabel : s.muted)}>
                    {starts ? weekday(d.date) : `${d.date?.slice(8, 10)} ${weekday(d.date)}`}
                    {d.date === now ? ' · TODAY' : ''}
                  </span>
                </div>
              );
            })}
          </div>

          <Lane name="Dest" grid={grid}>
            {runs.map((r) => (
              <Link
                key={`${r.name}${r.from}`}
                to="/trips/$tripId"
                params={{ tripId }}
                search={{ day: days[r.from]?.id }}
                className={cx(s.bar, s.dest, t.label)}
                style={span(r.from, r.to)}
              >
                {`// ${r.name.toUpperCase()} `}
                <span className={s.dim}>
                  {r.currency ?? ''} · {r.to - r.from + 1} days
                </span>
              </Link>
            ))}
          </Lane>

          <Lane name="Stay" grid={grid}>
            {stays.map((r) => {
              const a = Math.max(0, col(r.startLocal));
              const out = col(r.endLocal);
              const b = out < 0 ? days.length - 1 : Math.max(a, out - 1);
              const nights = out < 0 ? b - a + 1 : out - a;
              return (
                <div
                  key={r.id}
                  className={cx(s.bar, s.stay, t.bodyS)}
                  style={span(a, b)}
                  title={title(r.itemId)}
                >
                  {title(r.itemId)} <span className={cx(t.readoutS, s.muted)}>{nights} N</span>
                </div>
              );
            })}
          </Lane>

          <div className={s.lane} style={grid}>
            <div className={cx(t.label, s.laneName)}>Move</div>
            {days.map((d) => (
              <div key={d.id} className={cx(s.cell, d.date === now && s.todayCell)}>
                {model.reservations
                  .filter((r) => JOURNEY.has(r.kind) && r.startLocal?.slice(0, 10) === d.date)
                  .map((r) => (
                    <span key={r.id} className={cx(s.move, t.readoutS)} title={title(r.itemId)}>
                      {[
                        r.details.airlineCode && r.details.number
                          ? `${r.details.airlineCode} ${r.details.number}`
                          : (r.details.number ?? title(r.itemId).slice(0, 14)),
                        r.details.from && r.details.to ? `${r.details.from}→${r.details.to}` : null,
                        time(r.startLocal),
                      ]
                        .filter(Boolean)
                        .map((line) => (
                          <span key={line} className={s.moveLine}>
                            {line}
                          </span>
                        ))}
                    </span>
                  ))}
              </div>
            ))}
          </div>

          <div className={s.lane} style={grid}>
            <div className={cx(t.label, s.laneName)}>Stops</div>
            {days.map((d) => (
              <Link
                key={d.id}
                to="/trips/$tripId"
                params={{ tripId }}
                search={{ day: d.id }}
                className={cx(s.cell, d.date === now && s.todayCell)}
                aria-label={`Day ${d.number}: ${d.items.length} stops`}
              >
                <span className={s.dots}>
                  {d.items.slice(0, 12).map((p) => (
                    <i
                      key={p.item.id}
                      style={{
                        background: d.date < now ? hue(d.number) : undefined,
                        borderColor: hue(d.number),
                      }}
                    />
                  ))}
                </span>
              </Link>
            ))}
          </div>

          <div className={s.lane} style={grid}>
            <div className={cx(t.label, s.laneName)}>Track</div>
            {days.map((d) => {
              const m = model.segments
                .filter((x) => x.userId === me.data.user.id && x.dateLocal === d.date)
                .reduce((a, x) => a + x.distanceM, 0);
              return (
                <div key={d.id} className={cx(s.cell, t.readoutS, s.muted, d.date === now && s.todayCell)}>
                  {m > 0 ? `${(m / 1000).toFixed(1)} km` : ''}
                </div>
              );
            })}
          </div>

          <div className={cx(s.lane, s.spend)} style={grid}>
            <div className={cx(t.label, s.laneName)}>Spend {home}</div>
            {days.map((d, i) => (
              <div
                key={d.id}
                className={cx(s.cell, d.date === now && s.todayCell)}
                title={format(spendBy[i] ?? 0, home)}
              >
                <i
                  className={cx(s.spendBar, d.date === now && s.todayBar, d.date > now && s.future)}
                  style={{ height: `${((spendBy[i] ?? 0) / maxSpend) * 100}%` }}
                />
              </div>
            ))}
          </div>
        </section>
        <p className={cx(t.caption, s.muted, s.legend)}>
          Filled = past days · times in local zones · spend is shared expenses in {home}
        </p>
      </div>

      <div className={s.lower}>
        <section>
          <h2 className={cx(t.label, s.head)}>
            Bookings {model.reservations.length}
            {(() => {
              const missing = model.reservations.filter((r) => filesFor(r.itemId) === 0).length;
              return missing > 0 ? ` · ${missing} without a file` : '';
            })()}
          </h2>
          <ul className={s.list}>
            {model.reservations.map((r) => (
              <li key={r.id}>
                <span className={t.readoutS}>{flag(r.startLocal)}</span>
                <span className={t.bodyS}>{title(r.itemId)}</span>
                <span className={cx(t.readoutS, s.muted)}>{r.confirmation ?? ''}</span>
                <span className={cx(t.micro, filesFor(r.itemId) === 0 && s.muted)}>
                  {filesFor(r.itemId) > 0 ? `${filesFor(r.itemId)} file` : 'No file'}
                </span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h2 className={cx(t.label, s.head)}>Days</h2>
          <ul className={s.list}>
            {days.map((d, i) => (
              <li key={d.id} className={cx(d.date === now && s.todayRow)}>
                <span className={t.readoutS}>{String(d.number).padStart(2, '0')}</span>
                <Link
                  to="/trips/$tripId"
                  params={{ tripId }}
                  search={{ day: d.id }}
                  className={cx(t.bodyS, s.dayLink)}
                >
                  {[
                    d.title,
                    d.items
                      .slice(0, 4)
                      .map((p) => p.item.title)
                      .join(', '),
                  ]
                    .filter(Boolean)
                    .join(' · ') ||
                    d.destination?.name ||
                    flag(d.date)}
                </Link>
                <span className={cx(t.readoutS, s.num)}>
                  {spendBy[i] ? format(spendBy[i] as number, home) : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function Lane({ name, grid, children }: { name: string; grid: CSSProperties; children: React.ReactNode }) {
  return (
    <div className={s.lane} style={grid}>
      <div className={cx(t.label, s.laneName)}>{name}</div>
      {children}
    </div>
  );
}
